
import { Vue, Options } from "vue-class-component";
import UpdateNotification from "@/app/ui/views/incoming-process/sti-dest/component/update-notification.vue";
import formatByCountry from "@/app/infrastructures/misc/common-library/FormatByCountry";
import formatDateWithoutTime from "@/app/infrastructures/misc/common-library/FormatDateWithoutTime";
import { StiDestController } from "@/app/ui/controllers/StiDestController";
import Print from "../modules/print.vue";
import EmptyState from "@/app/ui/components/empty-list/index.vue";
import { StiDestData, StiDestDetail } from "@/domain/entities/StiDest";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { FlagsPermissionIncomingProcessSTIDest } from "@/feature-flags/flags-incoming-process-sti-dest";
import { formatDateNormal } from "@/app/infrastructures/misc/Utils";
import HeaderBeforeSTIDest from "@/app/ui/views/incoming-process/sti-dest/component/header-before-sti-dest.vue";
import HeaderSttNowDel from "@/app/ui/views/incoming-process/sti-dest/component/header-stt-now-del.vue";
import HeaderSttOverdueDel from "@/app/ui/views/incoming-process/sti-dest/component/header-stt-overdue-del.vue";
import HeaderSttBar from "@/app/ui/views/incoming-process/sti-dest/component/header-stt-bar.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

@Options({
  components: {
    UpdateNotification,
    Print,
    EmptyState,
    HeaderBeforeSTIDest,
    HeaderSttNowDel,
    HeaderSttOverdueDel,
    HeaderSttBar
  }
})
export default class STIDestListReverseScan extends Vue {
  refs: any = "";
  print: any = "";
  beforeMount() {
    StiDestController.setFilter({
      startDate: "",
      endDate: "",
      search: ""
    });
  }

  intervalId: number | undefined = undefined;

  mounted() {
    this.refs = this.$refs;
    StiDestController.setFirstPage();
    this.startRefreshTimer();
  }

  unmounted() {
    this.clearTimer();
  }
  updateNotificationModel = false;

  fetchStiDestList() {
    StiDestController.fetchStiDestList("v2");
  }

  fetchSummary() {
    StiDestController.fetchStiDestSummary();
    StiDestController.fetchGetPriorityDelSummary();
  }

  startRefreshTimer() {
    this.fetchSummary();
    // Set the interval to 10 minutes
    this.intervalId = window.setInterval(this.fetchSummary, 10 * 60 * 1000);
  }

  clearTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  onRefreshStiDestSummary() {
    this.clearTimer(); // Clear the existing interval
    this.startRefreshTimer(); // Start a new interval
  }

  get getTitle() {
    return this.$route.meta.title;
  }

  get ableToCreate() {
    return FlagsPermissionIncomingProcessSTIDest.permission_sti_dest_create.isEnabled();
  }

  get ableToDetail() {
    return FlagsPermissionIncomingProcessSTIDest.permission_sti_dest_detail.isEnabled();
  }

  // loading
  get isLoading() {
    return StiDestController.isLoading;
  }

  get isStiDestSummaryLoading() {
    return StiDestController.isStiDestSummaryLoading;
  }

  get isPriorSummaryLoading() {
    return StiDestController.isPriorSummaryLoading;
  }

  // error cause
  get errorCause() {
    return StiDestController.errorCause;
  }

  // filter date
  get startDate() {
    return StiDestController.filter.startDate;
  }

  get endDate() {
    return StiDestController.filter.endDate;
  }

  setDateRange(value: Array<any>) {
    StiDestController.setStartDate(value[0]);
    StiDestController.setEndDate(value[1]);
    StiDestController.setFirstPage();
    value[0] && value[1] && this.fetchStiDestList();
  }

  // filter search
  get searchValue() {
    return StiDestController.filter.search;
  }

  onSearch(value: string) {
    StiDestController.setSearch(value);
    StiDestController.setFirstPage();
    this.fetchStiDestList();
  }

  clearSearch() {
    StiDestController.setSearch("");
    this.fetchStiDestList();
  }

  // go to update sti-dest
  goToUpdate() {
    this.$router.push("/sti-dest/update");
  }

  goToDetail(item: StiDestData) {
    this.$router.push(`/sti-dest/detail/${item.stiDestId}`);
  }

  // Table
  get columns() {
    return [
      {
        name: this.$t("stiDest.table.no"),
        styleHead: "w-12 text-left whitespace-no-wrap",
        render: (item: any, index: number) => {
          return `<div class="overflow-ellipsis text-left">${index +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.manifestId"),
        styleHead: "w-28 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left">${item.stiDestId}</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.totalStt"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left" data-testid="incoming-sti-dest-total-stt">${formatByCountry(
            item.stiDestTotalStt,
            { type: "quantity" }
          )}</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.totalKoli"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left" data-testid="incoming-sti-dest-total-koli">${formatByCountry(
            item.stiDestTotalPiece,
            { type: "quantity" }
          )}</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.tanggalManifest"),
        styleHead: "w-40 text-left",
        render: (item: StiDestData) => {
          return `<div class="text-black-lp-300">${formatDateWithoutTime(
            item.stiDestCreatedAt
          )}</div>
            <div class="capitalize text-gray-lp-500 break-all">${
              item.stiDestCreatedBy
            }</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.transit"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left">${
            item.stiDestTransit != null
              ? formatByCountry(item.stiDestTransit, { type: "quantity" })
              : ""
          }</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.misroute"),
        styleHead: "w-24 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left">${
            item.stiDestMisroute != null
              ? formatByCountry(item.stiDestMisroute, { type: "quantity" })
              : ""
          }</div>`;
        }
      },
      {
        name: this.$t("stiDest.dashboard.segeraPod"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left font-semibold">${
            item.stiDestStatusReturnPod != null
              ? formatByCountry(item.stiDestStatusReturnPod, {
                  type: "quantity"
                })
              : ""
          }</div>`;
        }
      },
      {
        name: this.$t("stiDest.dashboard.segeraRts"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left font-semibold">${
            item.stiDestStatusReturnRts != null
              ? formatByCountry(item.stiDestStatusReturnRts, {
                  type: "quantity"
                })
              : ""
          }</div>`;
        }
      },
      {
        name: this.$t("stiDest.dashboard.segeraHal"),
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: StiDestData) => {
          return `<div class="overflow-ellipsis text-left font-semibold">${
            item.stiDestStatusReturnHal != null
              ? formatByCountry(item.stiDestStatusReturnHal, {
                  type: "quantity"
                })
              : ""
          }</div>`;
        }
      },
      {
        name: this.$t("stiDest.table.atur"),
        key: "button_column",
        styleHead: "w-24 text-left",
        styleCustom: "padding-action-sti",
        styleButton: (item: any) => {
          return {
            outline: true,
            icon: "option-gray",
            title: "",
            textColor: "black",
            color: "gray-lp-400",
            customClass: "button-option",
            clickFunction: () => null,
            groupOptions: [
              {
                icon: "print-summary",
                label: this.$t("stiDest.printRingkasan"),
                clickFunction: () => this.printSummary(item.stiDestId)
              },
              {
                icon: "printer",
                label: this.$t("stiDest.printPdf"),
                clickFunction: () => this.printManifest(item.stiDestId)
              },
              {
                icon: "download-gray",
                label: this.$t("stiDest.downloadExcel"),
                clickFunction: () =>
                  StiDestController.downloadExcelStiDestV2({
                    id: item.stiDestId,
                    version: "v2"
                  })
              }
            ]
          };
        }
      }
    ];
  }

  // custom button
  customButton = {
    outline: true,
    title: "Print",
    textColor: "black-lp-300",
    color: "gray-lp-400",
    small: false,
    clickFunction: (item: any) => this.refs.print.printManifest(item.stiDestId)
  };

  get pagination() {
    return StiDestController.stiDestList.pagination;
  }

  get stiDestList() {
    return StiDestController.stiDestList.data;
  }

  get stiDestSummary() {
    return StiDestController.stiDestSummary;
  }

  get priorityDelSummary() {
    return StiDestController.priorityDelSummary;
  }

  async printSummary(id: number) {
    const printManifest = await StiDestController.getDetailStiDest({
      id: Number(id),
      version: "v2"
    });

    if (printManifest) {
      const detail: StiDestDetail = StiDestController.stiDestDetail;
      const data: Array<any> = [
        {
          name: this.$t("stiDest.detail.arrivalCity"),
          value: `${detail.originCityCode} - ${detail.originConsolidator}`
        },
        {
          name: this.$t("stiDest.update.consolidatorName"),
          value: detail.stiDestPartnerName
        },
        {
          name: this.$t("stiDest.pdf.tanggalManifest"),
          value: formatDateNormal(detail.stiDestDate, "DD MMMM YYYY HH:mm:ss")
        },
        {
          name: this.$t("stiDest.pdf.totalSttKoli"),
          value: `${formatByCountry(detail.totalStt, {
            type: "quantity"
          })}/${formatByCountry(detail.totalPiece, { type: "quantity" })}`
        },
        {
          name: this.$t("stiDest.table.transit"),
          value:
            detail.stiDestTotalTransit === 0
              ? this.$t("stiDest.not")
              : formatByCountry(detail.stiDestTotalTransit, {
                  type: "quantity"
                })
        },
        {
          name: this.$t("stiDest.table.misroute"),
          value:
            detail.stiDestTotalMisroute === 0
              ? this.$t("stiDest.not")
              : formatByCountry(detail.stiDestTotalMisroute, {
                  type: "quantity"
                })
        },
        {
          name: this.$t("stiDest.dashboard.segeraPod"),
          value:
            detail.stiDestTotalStatusReturnPOD === 0
              ? this.$t("stiDest.not")
              : formatByCountry(detail.stiDestTotalStatusReturnPOD, {
                  type: "quantity"
                })
        },
        {
          name: this.$t("stiDest.dashboard.segeraRts"),
          value:
            detail.stiDestTotalStatusReturnRTS === 0
              ? this.$t("stiDest.not")
              : formatByCountry(detail.stiDestTotalStatusReturnRTS, {
                  type: "quantity"
                })
        },
        {
          name: this.$t("stiDest.dashboard.segeraHal"),
          value:
            detail.stiDestTotalStatusReturnHal === 0
              ? this.$t("stiDest.not")
              : formatByCountry(detail.stiDestTotalStatusReturnHal, {
                  type: "quantity"
                })
        }
      ];

      const print: any = await import(
        "@/app/ui/views/incoming-process/sti-dest/modules/print-reverse-scan.vue"
      );
      const logo = await import(
        `@/app/ui/assets/images/logo-${this.roleAccount}.png`
      );
      print.default.methods.printSummary(data, id, logo.default);
    }
  }

  async printManifest(id: number) {
    const printManifest = await StiDestController.getDetailStiDest({
      id: Number(id),
      version: "v2"
    });

    if (printManifest) {
      const detailManifest = StiDestController.stiDestDetail;
      const print: any = await import(
        "@/app/ui/views/incoming-process/sti-dest/modules/print-reverse-scan.vue"
      );
      const logo = await import(
        `@/app/ui/assets/images/logo-${this.roleAccount}.png`
      );
      print.default.methods.printManifest(detailManifest, logo.default);
    }
  }

  get roleAccount() {
    return (
      (this.dataProfile.account_type === "partner"
        ? this.dataProfile.account_type_detail.type
        : this.dataProfile.account_type) || "internal"
    );
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get isSidebarAndBreadcrumbV2() {
    return MainAppController.isSidebarAndBreadcrumbV2;
  }
}
